import { useState } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle, AlertCircle, Copy, RefreshCw } from 'lucide-react';

export default function CustomDomainManager({ storeId, domains = [], onUpdate }) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [dnsInstructions, setDnsInstructions] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [verifying, setVerifying] = useState(false);
  
  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Please enter a domain');
      return;
    }
    
    try {
      const response = await fetch(`${API}/stores/${storeId}/domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ domain: newDomain.trim().toLowerCase() }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      toast.success('Domain added! Configure DNS to verify.');
      setNewDomain('');
      setAddDialogOpen(false);
      onUpdate();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleRemoveDomain = async (domain) => {
    if (!window.confirm(`Remove ${domain}?`)) return;
    
    try {
      const response = await fetch(`${API}/stores/${storeId}/domains/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to remove domain');
      
      toast.success('Domain removed');
      onUpdate();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const fetchDNSInstructions = async (domain) => {
    try {
      const response = await fetch(
        `${API}/stores/${storeId}/domains/instructions?domain=${encodeURIComponent(domain)}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Failed to fetch instructions');
      
      const data = await response.json();
      setDnsInstructions(data);
      setSelectedDomain(domain);
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleVerifyDomain = async (domain) => {
    setVerifying(true);
    try {
      const response = await fetch(`${API}/stores/${storeId}/domains/${encodeURIComponent(domain)}/verify`, {
        method: 'POST',
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (data.verified) {
        toast.success(data.message);
        if (data.note) {
          toast.info(data.note, { duration: 5000 });
        }
        onUpdate();
      } else {
        toast.error(data.message);
        if (data.help) {
          toast.info(data.help, { duration: 8000 });
        }
      }
    } catch (error) {
      toast.error('Verification failed. Please check your DNS settings.');
    } finally {
      setVerifying(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-outfit font-bold">Custom Domains</h3>
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-domain-button" size="sm" className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Domain
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Custom Domain</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="domain">Domain Name</Label>
                <Input
                  id="domain"
                  data-testid="domain-input"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="shop.yourdomain.com"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Enter your domain (e.g., shop.example.com or example.com)
                </p>
              </div>
            </div>
            <Button data-testid="submit-domain-button" onClick={handleAddDomain} className="w-full rounded-full">
              Add Domain
            </Button>
          </DialogContent>
        </Dialog>
      </div>
      
      {domains.length === 0 ? (
        <div className="text-center py-8 border border-dashed rounded-xl">
          <p className="text-sm text-muted-foreground">No custom domains configured</p>
        </div>
      ) : (
        <div className="space-y-3">
          {domains.map((domainObj) => (
            <div
              key={domainObj.domain}
              data-testid={`domain-item-${domainObj.domain}`}
              className="border border-border rounded-xl p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{domainObj.domain}</span>
                    {domainObj.verified ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-yellow-500" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {domainObj.verified ? 'Verified' : 'Pending DNS configuration'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!domainObj.verified && (
                    <Button
                      data-testid={`verify-domain-button-${domainObj.domain}`}
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => handleVerifyDomain(domainObj.domain)}
                      disabled={verifying}
                    >
                      <RefreshCw className={`w-3 h-3 ${verifying ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                  <Button
                    data-testid={`remove-domain-button-${domainObj.domain}`}
                    size="sm"
                    variant="ghost"
                    className="rounded-full text-destructive"
                    onClick={() => handleRemoveDomain(domainObj.domain)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {!domainObj.verified && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full rounded-full text-xs"
                  onClick={() => fetchDNSInstructions(domainObj.domain)}
                >
                  View DNS Setup Instructions
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* DNS Instructions Dialog */}
      {dnsInstructions && (
        <Dialog open={!!dnsInstructions} onOpenChange={() => setDnsInstructions(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">Setup Instructions for {selectedDomain}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900 font-medium mb-2">
                  ✨ Free SSL with Cloudflare (Recommended)
                </p>
                <p className="text-sm text-blue-800">
                  We recommend using Cloudflare's free tier for automatic SSL certificates. This ensures your custom domain works with HTTPS at no cost to you or us!
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="font-bold text-lg">Step-by-Step Setup Guide:</h3>
                
                {/* Step 1 */}
                <div className="border-l-4 border-primary pl-4 py-2">
                  <h4 className="font-semibold mb-2">Step 1: Sign up for Cloudflare (Free)</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Go to <a href="https://dash.cloudflare.com/sign-up" target="_blank" rel="noopener noreferrer" className="text-primary underline">cloudflare.com</a> and create a free account (takes ~5 minutes)
                  </p>
                </div>

                {/* Step 2 */}
                <div className="border-l-4 border-primary pl-4 py-2">
                  <h4 className="font-semibold mb-2">Step 2: Add Your Domain to Cloudflare</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    In Cloudflare dashboard, click "Add Site" and enter: <span className="font-mono font-bold">{selectedDomain}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Select the Free plan when prompted.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="border-l-4 border-primary pl-4 py-2">
                  <h4 className="font-semibold mb-2">Step 3: Update Nameservers</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Cloudflare will provide you with 2 nameservers (like <span className="font-mono">alice.ns.cloudflare.com</span>)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Go to your domain registrar (where you bought the domain) and update the nameservers to Cloudflare's nameservers. This typically takes 5-10 minutes.
                  </p>
                </div>

                {/* Step 4 */}
                <div className="border-l-4 border-primary pl-4 py-2">
                  <h4 className="font-semibold mb-2">Step 4: Add DNS Record in Cloudflare</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Once nameservers are updated, add this DNS record in Cloudflare's DNS settings:
                  </p>
                  <div className="bg-muted rounded-lg p-4 space-y-2">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-muted-foreground">Type</Label>
                        <p className="font-mono font-bold">CNAME</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Name</Label>
                        <p className="font-mono font-bold">@</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Target</Label>
                        <div className="flex items-center gap-2">
                          <p className="font-mono font-bold text-xs break-all">fluence-mart.preview.emergentagent.com</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard('fluence-mart.preview.emergentagent.com')}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    <span className="font-semibold text-orange-600">Important:</span> Make sure the Cloudflare proxy is <span className="font-bold">enabled</span> (orange cloud icon should be lit up). This activates SSL!
                  </p>
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-900">
                      <span className="font-semibold">Note:</span> Use the Emergent hostname shown above, not fluencer.shop, to avoid Cloudflare proxy conflicts.
                    </p>
                  </div>
                </div>

                {/* Step 5 */}
                <div className="border-l-4 border-green-500 pl-4 py-2 bg-green-50">
                  <h4 className="font-semibold mb-2">Step 5: Wait for SSL Activation</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    SSL certificate provisioning is automatic and typically completes within 5-15 minutes.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    You can verify by visiting <span className="font-mono">https://{selectedDomain}</span> - if you see a lock icon, SSL is active!
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">📝 Quick Checklist:</h4>
                <div className="space-y-1 text-sm">
                  <div>☐ Cloudflare account created</div>
                  <div>☐ Domain added to Cloudflare</div>
                  <div>☐ Nameservers updated at registrar</div>
                  <div>☐ CNAME record added in Cloudflare</div>
                  <div>☐ Orange cloud (proxy) enabled</div>
                  <div>☐ SSL certificate active (lock icon in browser)</div>
                </div>
              </div>

              <div className="bg-gray-50 border rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">Need Help?</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Cloudflare's setup wizard is very user-friendly and walks you through each step.
                </p>
                <p className="text-sm text-muted-foreground">
                  Cloudflare docs: <a href="https://developers.cloudflare.com/dns/zone-setups/full-setup/" target="_blank" rel="noopener noreferrer" className="text-primary underline">Getting Started Guide</a>
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
